import { get } from "lodash";
import moment from "moment";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import AppBar from "src/components/AppBar/AppBar";
import GuardedComponent from "src/components/GuardedComponent/GuardedComponent";
import Icon, { IconName } from "src/components/Icon/Icon";
import StatusBadge from "src/components/StatusBadgeV2/StatusBadgeV2";
import StatusFilterTool from "src/components/TableV2/StatusFilter";
import { IIOCampaignResponse } from "src/store/models/campaign.model";
import { UserRole } from "src/store/models/user.model";
import { openUrl } from "src/util/util.navigation";
import FilterContainer from "../../components/TableV2/FilterContainer";
import SearchFieldTool from "../../components/TableV2/SearchField";
import DisTable from "../../components/TableV2/Table";
import Title from "../../components/TableV2/Title";
import * as S from "../../constants/StringConstants";
import { IRootState } from "../../store/reducers";
import { fetchCampaignList } from "../../store/reducers/campaign";
import { formatDateTime } from "../CampaignDetail/utils";
import "./Campaigns.scss";

interface StepsInfo {
  done: number;
  todo: number;
  nextDelivery?: string;
}

const parseSteps = (item: IIOCampaignResponse): StepsInfo => {
  if (item.stepsInfo) {
    const dones = [
      ...new Set(
        item.stepsInfo.filter(step => moment.utc(step.dueDateTime).isBefore(moment())).map(step => step.campaignStepId),
      ),
    ];
    const todos = [
      ...new Set(
        item.stepsInfo.filter(step => moment.utc(step.dueDateTime).isAfter(moment())).map(step => step.campaignStepId),
      ),
    ].sort();
    let nextDelivery;
    if (todos.length) {
      const nextStep = item.stepsInfo.find(step => step.campaignStepId === todos[0]);
      nextDelivery = nextStep.dueDateTime;
    }
    return { done: dones.length, todo: todos.length, nextDelivery };
  }
  return { done: 0, todo: 0 };
};

const getStatus = (item: IIOCampaignResponse): { label: string; color: string } => {
  const { saveAsDraft } = item;
  if (saveAsDraft) {
    return { label: "Draft", color: "draft" };
  }
  const { done, todo } = parseSteps(item);
  if (!(done || todo)) {
    return { label: "", color: "" };
  }
  if (todo === 0) {
    return { label: "Completed", color: "completed" };
  } else if (done === 0) {
    return { label: "Scheduled", color: "scheduled" };
  } else {
    return { label: "In Progress", color: "inprogress" };
  }
};

interface ICampaignProps extends StateProps, DispatchProps, RouteComponentProps {}

const StatusOptions = [
  { key: S.CAMP_OPTION_IO, value: "1" },
  { key: S.CAMP_OPTION_PROPOSAL, value: "2" },
];

export interface ICampaignState {
  open: boolean;
  campaignTypeIndex: number;
  optionsOpen: boolean;
}

class Campaigns extends React.Component<ICampaignProps, ICampaignState> {
  state = {
    ...this.state,
    optionsOpen: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      open: false,
      campaignTypeIndex: 0,
    };
  }

  toggleOptions = () => {
    this.setState({ optionsOpen: !this.state.optionsOpen });
  };

  render() {
    const {
      fetchCampaignList,
      campaign: { map, projection, total, listOptions, loading },
    } = this.props;
    const columns = [
      {
        key: "id",
        title: S.CAMP_COL_ID,
        style: { width: "2rem" },
        sortKey: "id",
      },
      {
        key: "campaignName",
        title: S.CAMP_COL_NAME,
        style: { minWidth: "15rem", width: "15rem" },
        sortKey: "campaignName",
      },
      {
        key: "carrierName",
        title: S.CAMP_CARRIER_NAME,
        style: { minWidth: "10rem", width: "10rem" },
        sortKey: "carrierName",
      },
      {
        key: "currentStep",
        title: S.CAMP_COL_CURRENT_STEP,
        style: { width: "14rem" },
        sortKey: "currentStep",
        render: item => `${parseSteps(item).done}/${item.noOfSteps}`,
      },
      {
        key: "nextDelivery",
        title: S.CAMP_COL_NEXT_DELIVERY,
        style: { width: "14rem" },
        sortKey: "nextDelivery",
        render: item => {
          const { nextDelivery } = parseSteps(item);
          if (nextDelivery) {
            return formatDateTime(nextDelivery);
          }
          return "";
        },
      },
      {
        key: "noOfContacts",
        title: S.CAMP_COL_TOTAL_CONTACTS,
        style: { width: "10rem" },
        sortKey: "noOfContacts",
      },
      {
        key: "totalMessagesEmail",
        title: S.CAMP_COL_TOTAL_EMAILS,
        style: { width: "9rem" },
        sortKey: "totalMessagesEmail",
      },
      {
        key: "totalMessagesSMS",
        title: S.CAMP_COL_TOTAL_SMS,
        style: { width: "7rem" },
        sortKey: "totalMessagesSMS",
      },
      {
        key: "errors",
        title: S.CAMP_COL_ERRORS,
        style: { width: "8rem" },
        render: item => {
          if (item.errorMessagesEmail || item.errorMessagesSMS) {
            return item.errorMessagesEmail + item.errorMessagesSMS;
          } else {
            return "";
          }
        },
        sortKey: "errors",
      },
      {
        key: "updatedBy",
        title: S.CAMP_COL_UPDATED_BY,
        style: { width: "11rem" },
        sortKey: "updatedBy",
      },
      {
        key: "updatedDate",
        title: S.CAMP_COL_UPDATED_DATE,
        style: { width: "12rem" },
        sortKey: "updatedDate",
        render: item => {
          if (item.updatedDate) {
            return moment(item.updatedDate).format("L");
          } else {
            return "";
          }
        },
      },
      {
        key: "status",
        title: S.CAMP_COL_STATUS,
        render: item => {
          const { label, color } = getStatus(item);
          return <StatusBadge status={color} label={label} />;
        },
        style: { width: "5rem" },
      },
    ];

    const toggleDrawer = (newOpen: boolean) => () => {
      this.setState({ open: newOpen });
    };

    return (
      <div className="table-view-v2__content">
        <div className="table-view__header-buttons-container">
          <AppBar icon={IconName.Campaigns} title={S.CAMP_TITLE} loading={loading}>
            <div className="table-view__buttons-container">
              <GuardedComponent
                authorizedRoles={[
                  UserRole.Admin,
                  UserRole.ProposalSpecialist,
                  UserRole.BranchManager,
                  UserRole.UnderwritingAssociate,
                  UserRole.IOSpecialist,
                ]}>
                <button className="side-nav__add-button" onClick={() => openUrl("/campaigns/new", true)}>
                  <Icon name={IconName.Plus} />
                  {S.NEW_CAMPAIGN}
                </button>

                <button className="side-nav__add-button" onClick={() => openUrl("/templates/new", true)}>
                  <Icon name={IconName.Plus} />
                  {S.NEW_TEMPLATE}
                </button>
              </GuardedComponent>
            </div>
          </AppBar>
        </div>

        <DisTable<IIOCampaignResponse>
          columns={columns}
          initialState={{
            map,
            projection,
            total,
            ...listOptions,
            rows: 100,
            query: { campaignType: StatusOptions[0].value },
          }}
          onRowClick={(rowData: any) => {
            if (rowData.saveAsDraft) {
              this.props.history.push(`/campaigns/new?id=${rowData.id}`);
            } else {
              this.props.history.push(`/campaigns/${rowData.id}`);
            }
          }}
          onUpdate={async ({ page, rows, sort, query }) => {
            const result: any = await fetchCampaignList({ page, rows, sort, query });
            return {
              total: get(result, "value.data.totalRows") as number,
              data: get(result, "value.data.items") as IIOCampaignResponse[],
            };
          }}>
          <FilterContainer
            left={<Title text={S.CAMP_TABLE_TITLE} />}
            right={
              <>
                <StatusFilterTool
                  statusList={StatusOptions}
                  selectedIndex={this.state.campaignTypeIndex}
                  fieldName="campaignType"
                  fieldLabel={S.CAMP_TYPE}
                />

                <SearchFieldTool />

                <Dropdown
                  className="options-filter__dropdown"
                  isOpen={this.state.optionsOpen}
                  toggle={this.toggleOptions}
                  direction="down">
                  <DropdownToggle tag="span" onClick={this.toggleOptions}>
                    <Icon name={IconName.Options} />
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem href="/templates/all">{S.TPL_MANAGE_TEMPLATES}</DropdownItem>
                    <DropdownItem href="/preproposals/all">{S.PRP_TITLE}</DropdownItem>
                    <DropdownItem href="/dnc/email/all">{S.DNC_EMAIL_SIDEBAR_TITLE}</DropdownItem>
                    <DropdownItem href="/dnc/phone/all">{S.DNC_PHONE_SIDEBAR_TITLE}</DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </>
            }></FilterContainer>
        </DisTable>
      </div>
    );
  }
}

const mapStateToProps = ({ campaign }: IRootState) => ({ campaign });
const mapDispatchToProps = { fetchCampaignList };

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(Campaigns);
